import { useMemo } from "react";

import { SalesMastType } from "@/lib/gql/__generated__/graphql";

import type { QueryData, ViewData } from "../type";
import type {
	SearchResultTableRowDataBaseType,
	SearchResultTableRowDataECType,
	SearchResultTableRowDataReservationLocalCommissionType,
	SearchResultTableRowDataTicketType,
} from "../type/view-data";

type Props = {
	filteredQueryData: QueryData;
};

type Result = {
	viewData: ViewData;
};

export const useGetViewData = ({ filteredQueryData }: Props): Result => {
	const totalSearchResultCount = useMemo(() => filteredQueryData.length, [filteredQueryData]);
	const totalSalesAmountText = useMemo(() => {
		let amount = 0;

		filteredQueryData.forEach((item) => {
			amount += item.income + (item.commission ?? 0);
			amount -= item.commission ?? 0;
		});

		return amount.toLocaleString();
	}, [filteredQueryData]);

	const rows = filteredQueryData.map((item) => {
		const totalIncome = item.income + (item.commission ?? 0);
		const purchaseItemName = (function () {
			switch (item.salesMastType) {
				case SalesMastType.Ec:
					return "EC利用料金";
				case SalesMastType.ReservationCredit || SalesMastType.ReservationLocal:
					return "宿泊料金";
				case SalesMastType.TicketBasic:
					return "CHILLNN 基本プラン利用料金";
				case SalesMastType.TicketPro:
					return "CHILLNN 定額チケット利用料金";
				case SalesMastType.ReservationLocalCommission:
					return "手数料（現地決済分・一括）";
				default:
					return "不明";
			}
		})();
		const buyerName = (function () {
			switch (item.salesMastType) {
				case SalesMastType.Ec:
					return item.infoEC?.guestName || "";
				case SalesMastType.ReservationCredit || SalesMastType.ReservationLocal:
					return item.infoReservation?.guestName || "";
				case SalesMastType.TicketBasic:
				case SalesMastType.TicketPro:
					return item.infoTicket?.billingHotelName || "";
				case SalesMastType.ReservationLocalCommission:
					return "-";
				default:
					return "";
			}
		})();

		const base = {
			hotelID: item.hotelID,

			income: item.income,
			commission: item.commission ?? undefined,
			totalIncome,
			isPlus: totalIncome >= 0,

			purchaseItemName,
			buyerName,

			cardBrand: item.card ?? null,

			transferredAt: item.transferredAt,
		} satisfies SearchResultTableRowDataBaseType;

		const row: ViewData["searchResultTableData"]["rows"][number] = (function () {
			switch (item.salesMastType) {
				case SalesMastType.Ec:
					return {
						...base,
						salesMastType: SalesMastType.Ec,

						guestAddress: item.infoEC?.guestAddress ?? "",
						guestEmail: item.infoEC?.guestEmail ?? "",
						guestName: item.infoEC?.guestName ?? "",
						guestPostalCode: item.infoEC?.guestPostalCode ?? "",
						guestTel: item.infoEC?.guestTel ?? "",

						userECReceiptID: item.infoEC?.userECReceiptID ?? "",
					} satisfies SearchResultTableRowDataECType;
				case SalesMastType.ReservationCredit || SalesMastType.ReservationLocal:
					return {
						...base,
						salesMastType: SalesMastType.ReservationCredit,

						guestAddress: item.infoReservation?.guestAddress ?? "",
						guestEmail: item.infoReservation?.guestEmail ?? "",
						guestName: item.infoReservation?.guestName ?? "",
						guestPostalCode: item.infoReservation?.guestPostalCode ?? "",
						guestTel: item.infoReservation?.guestTel ?? "",

						reservationID: item.infoReservation?.reservationID ?? "",
					};
				case SalesMastType.ReservationLocalCommission:
					return {
						...base,
						salesMastType: SalesMastType.ReservationLocalCommission,
					} satisfies SearchResultTableRowDataReservationLocalCommissionType;
				case SalesMastType.TicketBasic:
				case SalesMastType.TicketPro:
					return {
						...base,
						salesMastType: item.salesMastType,

						ticketName: item.infoTicket?.ticketName ?? "",
						validDtEnd: item.infoTicket?.validDtEnd ?? "",
						validMonth: String(item.infoTicket?.validMonth) ?? "",
						billingHotelName: item.infoTicket?.billingHotelName ?? "",
					} satisfies SearchResultTableRowDataTicketType;
				default:
					throw new Error("Invalid salesMastType");
			}
		})();

		return row satisfies ViewData["searchResultTableData"]["rows"][number];
	});

	const viewData = {
		searchResultTableData: {
			rows,
			totalSalesAmountText,
			searchResultCountText: totalSearchResultCount.toLocaleString(),
		},
	} satisfies ViewData;

	return {
		viewData,
	};
};
